import { useEffect, useState, useCallback } from "react";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";

const METADATA_TEMPLATE = `{
  "source_name": "Unknown Caller",
  "source_number": "081-000-0101",
  "destination_number": "+6652000000",
  "service_name": "Expat Tax Firm Voicemail",
  "call_time": "2026-07-20T09:15:00+07:00",
  "assumed_source_country": "Thailand",
  "timezone": "Asia/Bangkok",
  "language_hint": "en"
}`;

const PRIORITY_TONE = { low: "bg-slate-100 text-slate-600", normal: "bg-blue-50 text-blue-700",
  high: "bg-amber-50 text-amber-700", urgent: "bg-red-50 text-red-700" };
const REVIEW_TONE = { required: "bg-amber-50 text-amber-700 ring-amber-200",
  not_required: "bg-emerald-50 text-emerald-700 ring-emerald-200" };
const RESULT_TONE = { pass: "bg-emerald-600", partial_pass: "bg-amber-500", fail: "bg-red-600", pending: "bg-slate-400" };

const asText = (c) => (typeof c === "string" ? c
  : c && typeof c === "object" ? Object.entries(c).filter(([, v]) => v !== null && v !== "").map(([k, v]) => `${k}: ${v}`).join("; ")
  : String(c ?? ""));

function Card({ title, icon, children, action, testid }) {
  const Icon = Icons[icon] || Icons.Activity;
  return (
    <section data-testid={testid} className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-slate-500" />
          <h2 className="text-sm font-semibold text-slate-800 font-heading">{title}</h2>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function Field({ label, value }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex items-start justify-between gap-4 py-1.5 border-b border-slate-100 last:border-0">
      <span className="text-xs text-slate-500">{label}</span>
      <span className="text-sm text-slate-800 text-right break-words">{String(value)}</span>
    </div>
  );
}

export default function WorkflowHarness() {
  const [status, setStatus] = useState(null);
  const [workflows, setWorkflows] = useState([]);
  const [wfCode, setWfCode] = useState("WF-VOICE-001");
  const [persona, setPersona] = useState("Penny");
  const [metadata, setMetadata] = useState(METADATA_TEMPLATE);
  const [artifact, setArtifact] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [run, setRun] = useState(null);
  const [runs, setRuns] = useState([]);
  const [reviewNotes, setReviewNotes] = useState("");

  const loadRuns = useCallback(async () => {
    try {
      const r = await api.get(`/workflow-harness/runs?workflow_code=${wfCode}&limit=15`);
      setRuns(r.data.test_runs || []);
    } catch { /* ignore */ }
  }, [wfCode]);

  useEffect(() => {
    (async () => {
      try {
        const s = await api.get("/workflow-harness/status");
        setStatus(s.data);
        if (s.data.enabled) {
          const w = await api.get("/workflow-harness/workflows");
          setWorkflows(w.data.workflows || []);
        }
      } catch (e) {
        setStatus({ enabled: false, environment: "unknown" });
      }
    })();
  }, []);

  useEffect(() => { if (status?.enabled) loadRuns(); }, [status, loadRuns]);

  const wf = workflows.find((w) => w.workflow_code === wfCode);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/workflow-harness/audio", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setArtifact(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const onRun = async () => {
    setRunning(true); setError(null); setRun(null);
    let vars = {};
    try { vars = JSON.parse(metadata || "{}"); }
    catch { setError("Metadata is not valid JSON"); setRunning(false); return; }
    try {
      const body = { workflow_code: wfCode, persona, runtime_variables: vars };
      if (wf?.requires_audio) {
        if (!artifact) { setError("Please upload an audio file first"); setRunning(false); return; }
        body.attachment_id = artifact.artifact_id;
      }
      const r = await api.post("/workflow-harness/run", body);
      setRun(r.data);
      setReviewNotes(r.data.reviewer_notes || "");
      loadRuns();
    } catch (err) {
      setError(err?.response?.data?.detail?.message || err?.response?.data?.detail || "Run failed");
    } finally { setRunning(false); }
  };

  const markResult = async (result) => {
    if (!run) return;
    try {
      const r = await api.patch(`/workflow-harness/runs/${run.test_run_id}/review`,
        { reviewer_result: result, reviewer_notes: reviewNotes });
      setRun(r.data); loadRuns();
    } catch (err) { setError("Failed to save review"); }
  };

  if (status && !status.enabled) {
    return (
      <div className="p-8" data-testid="harness-disabled">
        <h1 className="text-2xl font-heading font-semibold text-slate-800 mb-2">AI Workflow Testing</h1>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800 text-sm">
          The AI Workflow Test Harness is disabled in this environment
          ({status.environment}). Enable <code>AI_WORKFLOW_VOICE_INTAKE_ENABLED</code> to use it.
        </div>
      </div>
    );
  }

  const so = run?.structured_output;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="workflow-harness-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-slate-800">AI Workflow Testing</h1>
          <p className="text-sm text-slate-500">Read-only dry-run certification harness · no production records are created.</p>
        </div>
        {status && (
          <span className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-600" data-testid="harness-env">
            {status.environment} · flag {status.enabled ? "ON" : "OFF"}
          </span>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm" data-testid="harness-error">
          {typeof error === "string" ? error : JSON.stringify(error)}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* CONFIG */}
        <Card title="Dry-Run Configuration" icon="SlidersHorizontal" testid="harness-config">
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-slate-500">Workflow</label>
              <select data-testid="harness-workflow-select" value={wfCode}
                onChange={(e) => setWfCode(e.target.value)}
                className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm">
                {workflows.map((w) => (
                  <option key={w.workflow_code} value={w.workflow_code}>
                    {w.workflow_code} — {w.workflow_name} v{w.workflow_version}
                  </option>
                ))}
              </select>
              {wf && <p className="text-[11px] text-slate-400 mt-1">
                {wf.category} · {wf.state_mutation_policy} · formats: {(wf.audio_formats || []).join(", ")}
              </p>}
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Persona</label>
              <div className="mt-1 flex gap-2">
                {(wf?.personas || ["Penny", "Allison"]).map((p) => (
                  <button key={p} data-testid={`harness-persona-${p.toLowerCase()}`}
                    onClick={() => setPersona(p)}
                    className={`px-3 py-1.5 rounded-lg text-sm border ${persona === p
                      ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200"}`}>
                    {p}
                  </button>
                ))}
              </div>
            </div>
            {wf?.requires_audio && (
              <div>
                <label className="text-xs font-medium text-slate-500">Voicemail Audio</label>
                <input type="file" accept=".mp3,.wav,.m4a,.webm,.mp4,audio/*" onChange={onUpload}
                  data-testid="harness-audio-input"
                  className="mt-1 block w-full text-sm text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-slate-100 file:text-slate-700" />
                {uploading && <p className="text-xs text-slate-400 mt-1">Uploading…</p>}
                {artifact && (
                  <p className="text-xs text-emerald-700 mt-1" data-testid="harness-audio-uploaded">
                    ✓ {artifact.original_filename} ({Math.round(artifact.size / 1024)} KB)
                  </p>
                )}
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-slate-500">Simulated Metadata (JSON test data)</label>
              <textarea data-testid="harness-metadata" value={metadata} onChange={(e) => setMetadata(e.target.value)}
                rows={9} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-xs font-mono" />
            </div>
            <button data-testid="harness-run-btn" onClick={onRun} disabled={running}
              className="w-full bg-slate-800 text-white rounded-lg py-2.5 text-sm font-medium disabled:opacity-50">
              {running ? "Running dry-run…" : "Run Dry-Run"}
            </button>
          </div>
        </Card>

        {/* RESULT */}
        <Card title="Result" icon="FileSearch" testid="harness-result"
          action={run && <span className={`text-xs px-2.5 py-1 rounded-full ring-1 ${REVIEW_TONE[so?.review?.status] || ""}`}
            data-testid="harness-review-status">review: {so?.review?.status}</span>}>
          {!run && <p className="text-sm text-slate-400">Run a dry-run to see results.</p>}
          {run && so && (
            <div className="space-y-4" data-testid="harness-result-content">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-700" data-testid="harness-classification">
                  {so.classification}
                </span>
                <span className="text-xs px-2 py-1 rounded bg-indigo-50 text-indigo-700" data-testid="harness-team">
                  {so.recommended_team?.display_name}
                </span>
                <span className={`text-xs px-2 py-1 rounded ${PRIORITY_TONE[so.priority] || ""}`} data-testid="harness-priority">
                  {so.priority}
                </span>
                <span className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-600">
                  schema {run.schema_validation_result?.ok ? "valid ✓" : "INVALID ✗"}
                </span>
              </div>

              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Confidence</div>
                <div className="grid grid-cols-5 gap-1 text-center" data-testid="harness-confidence">
                  {["transcription", "extraction", "classification", "routing", "overall"].map((k) => (
                    <div key={k} className="bg-slate-50 rounded p-1.5">
                      <div className={`text-sm font-semibold ${(so.confidence?.[k] ?? 0) < 0.85 ? "text-amber-600" : "text-emerald-600"}`}>
                        {(so.confidence?.[k] ?? 0).toFixed(2)}
                      </div>
                      <div className="text-[9px] text-slate-400 uppercase">{k.slice(0, 5)}</div>
                    </div>
                  ))}
                </div>
              </div>

              {so.review?.reasons?.length > 0 && (
                <div data-testid="harness-review-reasons">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Review Reasons</div>
                  <ul className="text-sm text-amber-700 space-y-0.5">
                    {so.review.reasons.map((r, i) => <li key={i}>• {asText(r)}</li>)}
                  </ul>
                </div>
              )}

              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Services</div>
                <div className="flex flex-wrap gap-1">
                  {so.services_requested?.length ? so.services_requested.map((s) => (
                    <span key={s} className="text-xs px-2 py-0.5 rounded bg-teal-50 text-teal-700">{s}</span>
                  )) : <span className="text-xs text-slate-400">none</span>}
                </div>
              </div>

              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Caller</div>
                <div className="border border-slate-100 rounded-lg px-3">
                  <Field label="Spoken name" value={so.caller?.spoken_name} />
                  <Field label="Metadata name" value={so.caller?.metadata_name} />
                  <Field label="Raw number" value={so.caller?.raw_source_number} />
                  <Field label="Normalized" value={so.caller?.normalized_source_number} />
                  <Field label="Email" value={so.caller?.email} />
                  <Field label="Residence" value={so.caller?.residence} />
                </div>
              </div>

              {so.metadata_conflicts?.length > 0 && (
                <div data-testid="harness-conflicts">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Metadata Conflicts</div>
                  <ul className="text-sm text-red-700 space-y-0.5">
                    {so.metadata_conflicts.map((c, i) => <li key={i}>• {asText(c)}</li>)}
                  </ul>
                </div>
              )}

              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Summary</div>
                <p className="text-sm text-slate-700">{so.summary}</p>
              </div>

              <details data-testid="harness-transcripts">
                <summary className="text-xs font-semibold text-slate-500 cursor-pointer">Transcripts & raw output</summary>
                <div className="mt-2 space-y-2">
                  <div>
                    <div className="text-[11px] text-slate-400">Raw transcript</div>
                    <p className="text-xs text-slate-600 bg-slate-50 rounded p-2 whitespace-pre-wrap">{so.raw_transcript}</p>
                  </div>
                  <div>
                    <div className="text-[11px] text-slate-400">Cleaned transcript</div>
                    <p className="text-xs text-slate-600 bg-slate-50 rounded p-2 whitespace-pre-wrap">{so.cleaned_transcript}</p>
                  </div>
                  <div>
                    <div className="text-[11px] text-slate-400">Structured JSON</div>
                    <pre className="text-[10px] text-slate-600 bg-slate-900 text-slate-100 rounded p-2 overflow-auto max-h-64">
{JSON.stringify(so, null, 2)}</pre>
                  </div>
                </div>
              </details>

              {/* Reviewer */}
              <div className="border-t border-slate-100 pt-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Reviewer Outcome</div>
                <textarea data-testid="harness-review-notes" value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)}
                  rows={2} placeholder="Reviewer notes…"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs mb-2" />
                <div className="flex gap-2">
                  {[["pass", "Pass"], ["partial_pass", "Partial"], ["fail", "Fail"]].map(([v, l]) => (
                    <button key={v} data-testid={`harness-mark-${v}`} onClick={() => markResult(v)}
                      className={`px-3 py-1.5 rounded-lg text-sm text-white ${RESULT_TONE[v]} ${run.reviewer_result === v ? "ring-2 ring-offset-1 ring-slate-400" : "opacity-90"}`}>
                      {l}
                    </button>
                  ))}
                  {run.reviewer_result !== "pending" && (
                    <span className="text-xs text-slate-500 self-center ml-2">saved: {run.reviewer_result}</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* RUN HISTORY */}
      <Card title="Recent Dry-Runs" icon="History" testid="harness-history"
        action={<button onClick={loadRuns} data-testid="harness-refresh-runs" className="text-xs text-slate-500 hover:text-slate-800">Refresh</button>}>
        {runs.length === 0 ? <p className="text-sm text-slate-400">No dry-runs yet.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-100">
                <th className="py-2">When</th><th>Persona</th><th>Classification</th><th>Team</th>
                <th>Overall</th><th>Review</th><th>Result</th>
              </tr></thead>
              <tbody data-testid="harness-runs-table">
                {runs.map((r) => (
                  <tr key={r.test_run_id} className="border-b border-slate-50">
                    <td className="py-2 text-slate-500 text-xs">{new Date(r.created_at).toLocaleString()}</td>
                    <td>{r.persona}</td>
                    <td>{r.structured_output?.classification}</td>
                    <td className="text-xs">{r.structured_output?.recommended_team?.display_name}</td>
                    <td>{(r.confidence_scores?.overall ?? 0).toFixed(2)}</td>
                    <td className="text-xs">{r.review_status}</td>
                    <td><span className={`text-white text-[10px] px-1.5 py-0.5 rounded ${RESULT_TONE[r.reviewer_result]}`}>{r.reviewer_result}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
